import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import {
  fetchAllInvoices,
  summarizeInvoices,
  billingYearRange,
  currentMonth,
  getUnitBilling,
  type UnitBilling,
  type ThisMonthStatus,
  type PublicInvoice,
} from '../../lib/ops-api';
import { billingStory, buildBillingInsights, monthsOfYear, type BillingInsights, type BillingStory } from '../../lib/billing-insights';
import { buildAnnualCsv, buildMonthlyCsv, downloadCsv } from '../../lib/annual-export';
import { dailyPayments } from '../../lib/daily-payments';
import { DailyPaymentsCard } from '../../components/billing/DailyPaymentsCard';
import { BillingInsightsPanel } from '../../components/billing/BillingInsightsPanel';
import { KpiCards } from '../../components/billing/KpiCards';
import { StatusBreakdownBar } from '../../components/billing/StatusBreakdownBar';
import { formatRupiah, formatDate } from '../../lib/format';
import { extractApiError } from '../../lib/api-client';
import { useT, type TFunc } from '../../i18n';
import { labelRepeatsAddress, unitAddress } from '../../lib/unit-address';

const rp = (cents: number) => `Rp ${formatRupiah(cents)}`;

/** Compact Indonesian amount for tight cells: 150rb, 1,5jt. Full value goes in tooltips. */
function compactRp(cents: number): string {
  const idr = Math.floor(cents / 100);
  if (idr >= 1_000_000) {
    const jt = idr / 1_000_000;
    return `${jt.toFixed(jt >= 10 ? 0 : 1).replace('.', ',')}jt`;
  }
  if (idr >= 1_000) return `${Math.round(idr / 1000)}rb`;
  return String(idr);
}

// status -> i18n key
const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'rpt.statusDraft',
  OPEN: 'rpt.statusOpen',
  PAID: 'rpt.statusPaid',
  OVERDUE: 'rpt.statusOverdue',
  VOID: 'rpt.statusVoid',
};

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('id-ID', { month: 'short', year: 'numeric' });
}

export function ReportsPage() {
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const t = useT();
  // Each tab draws these into its own toolbar row, beside its controls.
  const tabs = (
    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
      <Tab label={t('rpt.tabSummary')} />
      <Tab label={t('rpt.tabByUnit')} />
    </Tabs>
  );
  return (
    <Stack spacing={3}>
      {/* Title on the left, the page's one action on the right. */}
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t('rpt.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('rpt.subtitle')}
          </Typography>
        </div>
        <Button variant="outlined" startIcon={<NotificationsActiveIcon />} onClick={() => navigate('/reminders')}>
          {t('rpt.sendReminders')}
        </Button>
      </Stack>
      {tab === 0 ? <SummaryTab tabs={tabs} onShowUnits={() => setTab(1)} /> : <ByUnitTab tabs={tabs} />}
    </Stack>
  );
}

/** Tabs on the left and the tab's own controls on the right, over one divider. */
function TabToolbar({ tabs, children }: { tabs: ReactNode; children?: ReactNode }) {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={{ xs: 1.5, md: 2 }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ borderBottom: 1, borderColor: 'divider', pb: { xs: 1.5, md: 0 } }}
    >
      {tabs}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ py: { md: 1 } }}>
        {children}
      </Stack>
    </Stack>
  );
}

type PeriodMode = 'all' | 'current' | 'select' | 'year';

// Short month names for the annual spreadsheet's columns.
const SHORT_MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString('id-ID', { month: 'short' }),
);

// Long month names (id-ID) for the month picker — matches monthLabel's locale.
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString('id-ID', { month: 'long' }),
);

/** The collection rate a month should reach; months under it are flagged in the trend. */
const COLLECTION_TARGET = 0.9;

function SummaryTab({ tabs, onShowUnits }: { tabs: ReactNode; onShowUnits: () => void }) {
  const now = new Date();
  const t = useT();
  const [invoices, setInvoices] = useState<PublicInvoice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<PeriodMode>('current');
  const [selYear, setSelYear] = useState<number>(now.getFullYear());
  const [selMonth, setSelMonth] = useState<number>(now.getMonth() + 1); // 1-12
  const [units, setUnits] = useState<UnitBilling[] | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAllInvoices()
      .then(setInvoices)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
    getUnitBilling()
      .then(setUnits)
      .catch(() => setUnits(null));
  }, []);

  // The chart shows the chosen year in year mode, otherwise the last 12 months.
  // Arrears always describe today, whatever is picked.
  const insights = useMemo<BillingInsights | null>(
    () => (units ? buildBillingInsights(units, new Date(), 12, mode === 'year' ? monthsOfYear(selYear) : undefined) : null),
    [units, mode, selYear],
  );
  const story = useMemo<BillingStory | null>(
    () => (insights && units ? billingStory(insights, units.length, COLLECTION_TARGET) : null),
    [insights, units],
  );

  const downloadYear = () => {
    if (!units) return;
    const csv = buildAnnualCsv(units, selYear, {
      house: t('rpt.csvHouse'),
      billed: t('rpt.csvBilled'),
      paid: t('rpt.csvPaid'),
      owed: t('rpt.csvOwed'),
      total: t('rpt.csvTotal'),
      months: SHORT_MONTH_NAMES,
      status: { PAID: t('rpt.csvPaidCell'), UNPAID: t('rpt.csvUnpaidCell'), NONE: '-' },
    });
    downloadCsv(`laporan-ipl-${selYear}.csv`, csv);
  };

  /** One month ('YYYY-MM'), one row per house. */
  const downloadMonth = (month: string) => {
    if (!units) return;
    const csv = buildMonthlyCsv(units, month, {
      house: t('rpt.csvHouse'),
      status: t('rpt.csvStatus'),
      billed: t('rpt.csvBilled'),
      paid: t('rpt.csvPaid'),
      owed: t('rpt.csvOwed'),
      paidOn: t('rpt.csvPaidOn'),
      total: t('rpt.csvMonthTotal'),
      statuses: { PAID: t('rpt.csvPaidCell'), UNPAID: t('rpt.csvUnpaidCell'), NONE: '-' },
    });
    downloadCsv(`laporan-ipl-${month}.csv`, csv);
  };

  const years = useMemo(() => billingYearRange(invoices ?? []), [invoices]);
  const period =
    mode === 'all'
      ? 'all'
      : mode === 'current'
        ? currentMonth()
        : mode === 'year'
          ? String(selYear)
          : `${selYear}-${String(selMonth).padStart(2, '0')}`;
  const summary = useMemo(
    () => (invoices ? summarizeInvoices(invoices, period) : null),
    [invoices, period],
  );

  // Clicking a month on the chart opens that month's report.
  const openMonth = (ym: string) => {
    const [y, m] = ym.split('-').map(Number);
    if (!y || !m) return;
    setSelYear(y);
    setSelMonth(m);
    setMode(ym === currentMonth() ? 'current' : 'select');
    // The layout scrolls inside <main>, not the window, so scroll the report into view.
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const showArrears = () => document.getElementById('arrears')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // Daily payments only make sense for a single month.
  const daily = useMemo(
    () => (invoices && (mode === 'current' || mode === 'select') ? dailyPayments(invoices, period, new Date()) : null),
    [invoices, mode, period],
  );

  const periodLabel =
    period === 'all' ? t('rpt.periodAll') : mode === 'year' ? t('rpt.yearLabel', { year: selYear }) : monthLabel(period);
  const downloadLabel =
    mode === 'year' ? t('rpt.downloadYear', { year: selYear }) : t('rpt.downloadMonth', { month: monthLabel(period) });

  // Tabs on the left, the period and the download on the right, in one row.
  const toolbar = (
    <TabToolbar tabs={tabs}>
        <TextField
          select
          size="small"
          label={t('rpt.period')}
          value={mode}
          onChange={(e) => setMode(e.target.value as PeriodMode)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">{t('rpt.periodAll')}</MenuItem>
          <MenuItem value="current">{t('rpt.periodCurrent')}</MenuItem>
          <MenuItem value="select">{t('rpt.periodSelect')}</MenuItem>
          <MenuItem value="year">{t('rpt.periodYear')}</MenuItem>
        </TextField>
        {mode === 'select' || mode === 'year' ? (
          <TextField
            select
            size="small"
            label={t('rpt.year')}
            value={selYear}
            onChange={(e) => setSelYear(Number(e.target.value))}
            sx={{ minWidth: 110 }}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {mode === 'select' ? (
          <TextField
            select
            size="small"
            label={t('rpt.month')}
            value={selMonth}
            onChange={(e) => setSelMonth(Number(e.target.value))}
            sx={{ minWidth: 150 }}
          >
            {MONTH_NAMES.map((name, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {/* A short label; the tooltip names the file's period. All-time has no download. */}
        {mode !== 'all' ? (
          <Tooltip title={downloadLabel}>
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={mode === 'year' ? downloadYear : () => downloadMonth(period)}
                disabled={!units}
                aria-label={downloadLabel}
              >
                {t('rpt.download')}
              </Button>
            </span>
          </Tooltip>
        ) : null}
    </TabToolbar>
  );

  if (error) return <Stack spacing={3}>{toolbar}<Alert severity="error">{error}</Alert></Stack>;
  if (loading)
    return (
      <Stack spacing={3}>
        {toolbar}
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </Stack>
    );
  if (!summary) return toolbar;

  return (
    <Stack ref={topRef} spacing={3} sx={{ scrollMarginTop: 88 }}>
      {toolbar}
      <KpiCards
        summary={summary}
        trend={insights?.trend ?? null}
        story={story}
        target={COLLECTION_TARGET}
        onShowUnits={onShowUnits}
        onShowArrears={showArrears}
      />
      <StatusBreakdownBar summary={summary} periodLabel={periodLabel} />
      {daily ? <DailyPaymentsCard data={daily} monthLabel={monthLabel(period)} /> : null}
      {insights && insights.trend.some((m) => m.collectedCents + m.outstandingCents > 0) ? (
        <BillingInsightsPanel
          insights={insights}
          story={story}
          target={COLLECTION_TARGET}
          title={mode === 'year' ? t('bill.yearTitle', { year: selYear }) : undefined}
          onMonthClick={openMonth}
        />
      ) : null}
    </Stack>
  );
}

interface MonthCell {
  ym: string; // "YYYY-MM"
  label: string; // short month, e.g. "Jun"
  status: ThisMonthStatus; // PAID | UNPAID | NONE
  amountCents: number; // sum of that month's invoice totals (0 if not billed)
  paidAt: string | null;
}

/** Trailing 12 months including the current one, oldest → newest. */
function buildYearStrip(unit: UnitBilling): MonthCell[] {
  const now = new Date();
  const cells: MonthCell[] = [];
  for (let k = 11; k >= 0; k--) {
    const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const monthInvs = unit.invoices.filter((i) => i.issue_date.slice(0, 7) === ym);
    let status: ThisMonthStatus = 'NONE';
    if (monthInvs.length > 0) {
      status = monthInvs.some((i) => i.status === 'OPEN' || i.status === 'OVERDUE')
        ? 'UNPAID'
        : 'PAID';
    }
    const paid = monthInvs.find((i) => i.status === 'PAID' && i.paid_at);
    cells.push({
      ym,
      label: d.toLocaleDateString('id-ID', { month: 'short' }),
      status,
      amountCents: monthInvs.reduce((s, i) => s + i.total_cents, 0),
      paidAt: paid?.paid_at ?? null,
    });
  }
  return cells;
}

function cellSx(status: ThisMonthStatus) {
  if (status === 'PAID') return { bgcolor: 'success.light', color: 'success.dark', borderColor: 'success.main' };
  if (status === 'UNPAID') return { bgcolor: 'error.light', color: 'error.dark', borderColor: 'error.main' };
  return { bgcolor: 'grey.100', color: 'text.secondary', borderColor: 'divider' };
}

function cellTooltip(c: MonthCell, t: TFunc): string {
  const m = monthLabel(c.ym);
  if (c.status === 'NONE') return t('rpt.tipNotBilled', { month: m });
  if (c.status === 'PAID') {
    return c.paidAt
      ? t('rpt.tipPaidOn', { month: m, amount: rp(c.amountCents), date: formatDate(c.paidAt.slice(0, 10)) })
      : t('rpt.tipPaid', { month: m, amount: rp(c.amountCents) });
  }
  return t('rpt.tipUnpaid', { month: m, amount: rp(c.amountCents) });
}

function YearStrip({ unit }: { unit: UnitBilling }) {
  const cells = buildYearStrip(unit);
  const t = useT();
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('rpt.last12Months', { unit: unitAddress(unit.block, unit.number) })}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {cells.map((c) => (
          <Tooltip key={c.ym} title={cellTooltip(c, t)} arrow>
            <Box
              sx={{
                width: 60,
                py: 0.5,
                px: 0.25,
                textAlign: 'center',
                borderRadius: 1,
                border: '1px solid',
                ...cellSx(c.status),
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, textTransform: 'capitalize' }}>
                {c.label}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: 10 }}>
                {c.status === 'NONE' ? '—' : compactRp(c.amountCents)}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <LegendSwatch status="PAID" label={t('rpt.legendPaid')} />
        <LegendSwatch status="UNPAID" label={t('rpt.legendUnpaid')} />
        <LegendSwatch status="NONE" label={t('rpt.legendNotBilled')} />
      </Stack>
    </Box>
  );
}

function LegendSwatch({ status, label }: { status: ThisMonthStatus; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 14, height: 14, borderRadius: 0.5, border: '1px solid', ...cellSx(status) }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

function ByUnitTab({ tabs }: { tabs: ReactNode }) {
  const t = useT();
  const [rows, setRows] = useState<UnitBilling[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<UnitBilling | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    getUnitBilling()
      .then(setRows)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = rows
      .slice()
      .sort((a, b) => (a.block + a.number).localeCompare(b.block + b.number, undefined, { numeric: true }));
    if (!needle) return sorted;
    return sorted.filter((r) =>
      `${r.block}${r.number} ${r.block}-${r.number} ${unitAddress(r.block, r.number)} ${r.label}`.toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const toolbar = (
    <TabToolbar tabs={tabs}>
      <TextField
        size="small"
        label={t('rpt.findUnit')}
        placeholder={t('rpt.findUnitPlaceholder')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ width: { xs: '100%', md: 260 } }}
      />
    </TabToolbar>
  );

  if (error) return <Stack spacing={3}>{toolbar}<Alert severity="error">{error}</Alert></Stack>;

  return (
    <Stack spacing={2}>
      {toolbar}
      <Typography variant="caption" color="text.secondary">
        {t('rpt.byUnitHelp')}
      </Typography>
      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : shown.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('rpt.noUnitsMatch')}</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>{t('rpt.colUnit')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{t('rpt.colThisMonth')}</TableCell>
                    <TableCell sx={{ width: 130 }}>{t('rpt.colMonthsBehind')}</TableCell>
                    <TableCell sx={{ width: 150 }}>{t('rpt.outstanding')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{t('rpt.colOldestUnpaid')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => {
                    const isOpen = expanded.has(r.unit_id);
                    return (
                      <Fragment key={r.unit_id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', '& > *': { borderBottom: isOpen ? 'unset' : undefined } }}
                          onClick={() => toggleExpand(r.unit_id)}
                        >
                          <TableCell sx={{ width: 48 }}>
                            <IconButton size="small" aria-label={isOpen ? t('rpt.collapse') : t('rpt.expand')}>
                              <KeyboardArrowDownIcon
                                fontSize="small"
                                sx={{
                                  transition: 'transform 0.2s',
                                  transform: isOpen ? 'rotate(180deg)' : 'none',
                                }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetail(r);
                              }}
                              sx={{ fontWeight: 700, color: 'inherit', textAlign: 'left' }}
                            >
                              {unitAddress(r.block, r.number)}
                            </Link>
                            {!labelRepeatsAddress(r.label, r.block, r.number) ? (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {' '}
                                · {r.label}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <ThisMonthChip status={r.thisMonth} />
                          </TableCell>
                          <TableCell>
                            {r.unpaidCount > 0 ? (
                              <Chip size="small" color="error" label={t('rpt.monthsBehindValue', { count: r.unpaidCount })} />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {t('rpt.upToDate')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{r.outstandingCents > 0 ? rp(r.outstandingCents) : '—'}</TableCell>
                          <TableCell>{r.oldestUnpaidMonth ? monthLabel(r.oldestUnpaidMonth) : '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }} colSpan={6}>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <YearStrip unit={r} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      <UnitInvoicesDialog unit={detail} onClose={() => setDetail(null)} />
    </Stack>
  );
}

function ThisMonthChip({ status }: { status: ThisMonthStatus }) {
  const t = useT();
  if (status === 'PAID') return <Chip size="small" color="success" label={t('rpt.legendPaid')} />;
  if (status === 'UNPAID') return <Chip size="small" color="error" label={t('rpt.legendUnpaid')} />;
  return <Chip size="small" label={t('rpt.legendNotBilled')} />;
}

function UnitInvoicesDialog({ unit, onClose }: { unit: UnitBilling | null; onClose: () => void }) {
  const t = useT();
  return (
    <Dialog open={!!unit} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {unit ? t('rpt.unitInvoicesTitle', { unit: unitAddress(unit.block, unit.number) }) : ''}
      </DialogTitle>
      <DialogContent>
        {unit ? (
          unit.invoices.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {t('rpt.noUnitInvoices')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('rpt.colMonth')}</TableCell>
                    <TableCell>{t('rpt.colAmount')}</TableCell>
                    <TableCell>{t('rpt.colStatus')}</TableCell>
                    <TableCell>{t('rpt.colPaidOn')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unit.invoices.map((inv: PublicInvoice) => (
                    <TableRow key={inv.id}>
                      <TableCell>{monthLabel(inv.issue_date.slice(0, 7))}</TableCell>
                      <TableCell>{rp(inv.total_cents)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABELS[inv.status] ? t(STATUS_LABELS[inv.status]) : inv.status}
                          color={inv.status === 'PAID' ? 'success' : inv.status === 'OVERDUE' ? 'error' : inv.status === 'OPEN' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{inv.paid_at ? formatDate(inv.paid_at.slice(0, 10)) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
